/**
 * Participant-specific admin handler
 * 
 * Extends base entity admin with Participant-specific functionality:
 * - Status filtering and updates
 * - Group filtering and assignment
 * - Workspace participant management
 * - Statistics
 */

import { useState, useCallback } from "react";
import type { partyStructureTypes } from "@pineseed/spec";
import { useBaseEntityAdmin, type EntityAdminHandler } from "./useEntityAdmin";
import type { UseAdminAppReturn } from "./useAdminApp";

type Participant = partyStructureTypes["Participant"];
type ParticipantStatus = Participant['status'];

export interface ParticipantAdminHandler extends EntityAdminHandler<Participant> {
  // Filtering
  filterByStatus: (status: ParticipantStatus | '') => Promise<void>;
  filterByGroup: (groupId: string) => Promise<void>;
  clearFilters: () => Promise<void>;
  
  // Quick actions
  updateStatus: (id: string, status: ParticipantStatus) => Promise<void>;
  
  // Statistics
  getStats: () => {
    total: number;
    byStatus: Record<string, number>;
    byType: Record<string, number>;
    byGroup: Record<string, number>;
  };
  
  // Current filters
  currentFilters: {
    status: ParticipantStatus | '';
    groupId: string;
  };
}

export function useParticipantAdmin(adminApp: UseAdminAppReturn): ParticipantAdminHandler {
  // Base CRUD operations
  // Pattern: LIST uses 'participants' (plural), CRUD uses 'participant' (singular)
  const baseHandler = useBaseEntityAdmin<Participant>({
    adminApp,
    endpoint: 'participants', // For GET /participants (list)
    crudEndpoint: 'participant', // For POST/PUT/DELETE /participant
    entityName: 'participant',
    extractFromResponse: (data: any) => data.participants || data || [],
  });

  // Current filters state
  const [currentFilters, setCurrentFilters] = useState<{
    status: ParticipantStatus | '';
    groupId: string;
  }>({
    status: '',
    groupId: '',
  });

  // Filter by status
  const filterByStatus = useCallback(async (status: ParticipantStatus | '') => {
    setCurrentFilters(prev => ({ ...prev, status }));
    
    if (!status) {
      await baseHandler.loadEntities();
      return;
    }

    baseHandler.setLoading(true);
    baseHandler.setError(null);
    
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/participants/status?workspaceId=${adminApp.workspaceId}&status=${status}`,
        {
          headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
          credentials: 'include',
        }
      );
      if (!response.ok) throw new Error('Failed to filter by status');
      const data = await response.json();
      baseHandler.setEntities(data.participants || data || []);
      console.log('Filtered by status:', status, data.participants?.length);
    } catch (error) {
      const errorMsg = 'Failed to filter by status';
      console.error(errorMsg, error);
      baseHandler.setError(errorMsg);
    } finally {
      baseHandler.setLoading(false);
    }
  }, [adminApp, baseHandler]);

  // Filter by group
  const filterByGroup = useCallback(async (groupId: string) => {
    setCurrentFilters(prev => ({ ...prev, groupId }));
    
    if (!groupId) {
      await baseHandler.loadEntities();
      return;
    }

    baseHandler.setLoading(true);
    baseHandler.setError(null);
    
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/participants/group?groupId=${groupId}`,
        {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );
      if (!response.ok) throw new Error('Failed to filter by group');
      const data = await response.json();
      baseHandler.setEntities(data.participants || data || []);
      console.log('Filtered by group:', groupId, data.participants?.length);
    } catch (error) {
      const errorMsg = 'Failed to filter by group';
      console.error(errorMsg, error);
      baseHandler.setError(errorMsg);
    } finally {
      baseHandler.setLoading(false);
    }
  }, [adminApp, baseHandler]);

  // Clear all filters
  const clearFilters = useCallback(async () => {
    setCurrentFilters({ status: '', groupId: '' });
    await baseHandler.loadEntities();
  }, [baseHandler]);

  // Quick status update
  const updateStatus = useCallback(async (id: string, status: ParticipantStatus) => {
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/participant/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
        body: JSON.stringify({ id, status }),
      });
      
      if (!response.ok) throw new Error('Failed to update status');
      
      console.log('Status updated:', id, status);
      
      // Update local state
      const updated = baseHandler.entities.map(p => 
        p.id === id ? { ...p, status } : p
      );
      baseHandler.setEntities(updated);
    } catch (error) {
      console.error('Failed to update status:', error);
      throw error;
    }
  }, [adminApp, baseHandler]);

  // Get statistics
  const getStats = useCallback(() => {
    const byStatus = baseHandler.entities.reduce((acc, p) => {
      acc[p.status] = (acc[p.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byType = baseHandler.entities.reduce((acc, p) => {
      acc[p.type] = (acc[p.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byGroup = baseHandler.entities.reduce((acc, p) => {
      const groupKey = p.groupId || 'No Group';
      acc[groupKey] = (acc[groupKey] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total: baseHandler.entities.length,
      byStatus,
      byType,
      byGroup,
    };
  }, [baseHandler.entities]);

  return {
    // Base operations
    ...baseHandler,
    
    // Participant-specific operations
    filterByStatus,
    filterByGroup,
    clearFilters,
    updateStatus,
    getStats,
    currentFilters,
  };
}

